/**
 * IST (Asia/Kolkata, UTC+5:30) calendar-day helpers for client-side gates.
 *
 * OnFlow's product semantics are anchored to the Indian user's calendar day
 * (ADR-0017, weekly review week-window, daily prompts). These helpers exist
 * so the web client can ask "did the user log X today?" without parsing
 * timezone offsets at every call site.
 *
 * Pure functions — same input always returns the same output. The optional
 * `now` parameter is the test seam.
 */

const IST_OFFSET = '+05:30';

/**
 * Returns the IST calendar date for `now` as a 'YYYY-MM-DD' string.
 * en-CA locale is used because it formats as ISO date naturally.
 */
export function todayISTKey(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * Returns the ISO-8601 UTC string for 00:00:00 IST on the current IST day.
 * Suitable as the `from` filter on /logs/weight queries.
 */
export function startOfTodayIST(now: Date = new Date()): string {
  return new Date(`${todayISTKey(now)}T00:00:00.000${IST_OFFSET}`).toISOString();
}

/**
 * Returns the ISO-8601 UTC string for 23:59:59.999 IST on the current IST day.
 * Suitable as the `to` filter on /logs/weight queries (backend uses lte).
 */
export function endOfTodayIST(now: Date = new Date()): string {
  return new Date(`${todayISTKey(now)}T23:59:59.999${IST_OFFSET}`).toISOString();
}
